import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { FormInput } from "@/common/components/FormInput";
import { useAuth } from "@/rentos/ui";
import { useToast } from "@/common/hooks/useToast";

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, updatePassword } = useAuth();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword) {
      toast.warning("Enter your current (temporary) password.");
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      toast.warning("New password must be at least 8 characters.");
      return;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(newPassword)) {
      toast.warning("Password must contain uppercase, lowercase, number, and special character (@$!%*?&).");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      const result = await updatePassword({ currentPassword, newPassword });
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      toast.success("Password updated successfully! Redirecting...");
      setTimeout(() => navigate("/console", { replace: true }), 1000);
    } catch {
      toast.error("Failed to update password. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(15,118,110,0.14),_transparent_38%),linear-gradient(180deg,_var(--color-background),_var(--color-surface))] px-4 py-12">
      <div className="w-full max-w-md rounded-[2rem] border border-border bg-card p-8 shadow-premium sm:p-10">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
            <KeyRound className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Set Your Password
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Welcome{user?.name ? `, ${user.name}` : ""}! For security, please set a new password before continuing.
          </p>
        </div>

        {/* Warning banner */}
        <div className="mb-6 rounded-xl border border-warning/30 bg-warning/10 p-3 text-xs text-warning font-medium">
          Enter the temporary password you were given and choose a new secure password.
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput
            label="Temporary Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter your temporary password"
            required
          />
          <FormInput
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Choose a strong password"
            required
          />
          <FormInput
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter new password"
            required
          />

          {/* Password requirements */}
          <div className="rounded-xl bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
            <p className="font-semibold text-foreground">Password requirements:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>At least 8 characters</li>
              <li>One uppercase letter (A-Z)</li>
              <li>One lowercase letter (a-z)</li>
              <li>One number (0-9)</li>
              <li>One special character (@$!%*?&)</li>
            </ul>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full rounded-xl bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:bg-primary-hover transition-colors disabled:opacity-50"
          >
            {saving ? "Updating..." : "Set Password & Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
